
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import SearchBusPanel from "./SearchBusPanel";
import './Bus.css'; 

// Animation Hook
const useInView = (options) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.unobserve(entry.target);
            }
        }, options);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [ref, options]);

    return [ref, inView];
};

const SearchBus = () => {
    const navigate = useNavigate();
    
    // Animation Refs
    const [offersRef, offersInView] = useInView({ threshold: 0.1 });
    const [destinationsRef, destinationsInView] = useInView({ threshold: 0.1 });
    const [whyUsRef, whyUsInView] = useInView({ threshold: 0.1 });

    const handleSearch = (searchQuery) => {
        navigate("/bus/results", { state: { searchQuery } });
    };

    // --- Data Configuration ---
    const busDeals = [
        { 
            operator: 'Primo', 
            logoUrl: '/assets/images/buslogos/Primo.png', 
            title: 'On-Time Guarantee', 
            description: 'Get 25% refund if your bus is delayed.', 
            discount: 'Up to 25% OFF', 
            type: 'percent' 
        },
        { 
            operator: 'ZingBus', 
            logoUrl: '/assets/images/buslogos/Zingbus.png', 
            title: 'Premium Sleeper', 
            description: 'Experience luxury on wheels with lounges.', 
            discount: 'Flat ₹200 OFF', 
            type: 'flat' 
        },
        { 
            operator: 'IntrCity', 
            logoUrl: '/assets/images/buslogos/InterCity.png', 
            title: 'SmartBus Special', 
            description: 'Washrooms, WiFi and Live Tracking.', 
            discount: 'Starting @ ₹899', 
            type: 'price' 
        },
    ];

    const popularRoutes = [
        { name: 'Mumbai to Goa', image: 'https://images.unsplash.com/photo-1607577070449-531c2296916c?q=80&w=1025&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D' },
        { name: 'Delhi to Manali', image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=1000' },
        { name: 'Bangalore to Hyderabad', image: 'https://plus.unsplash.com/premium_photo-1682913629540-3857602b540c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8cmVzb3J0fGVufDB8fDB8fHww' },
        { name: 'Chennai to Pondicherry', image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWFsZGl2ZXN8ZW58MHx8MHx8fDA%3D' },
    ];

    const whyChooseUs = [
        { icon: 'ri-bus-wifi-line', title: 'Live Tracking', text: 'Track your bus in real-time and share location with family.' },
        { icon: 'ri-user-seat-line', title: 'Comfortable Seats', text: 'Sleeper, Seater, Semi-Sleeper - choose your comfort.' },
        { icon: 'ri-customer-service-2-line', title: '24/7 Support', text: 'Our dedicated team is here to help you around the clock.' },
    ];

    return (
        <>
            {/* HERO SECTION */}
            <div className="bus-hero-section">
                <div className="bus-hero-grid-container">
                    <div className="bus-hero-text-content">
                        <h1 className="bus-hero-heading">Book Bus Tickets.</h1>
                        <p className="bus-hero-subtitle">India's largest online bus ticketing platform</p>
                    </div>
                    
                    <div className="bus-hero-form-content">
                        <div className="bus-search-panel-container">
                             <SearchBusPanel onSearch={handleSearch} />
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="page-content" style={{ paddingTop: 0 }}>
                <div className="container">
                    
                    {/* OFFERS SECTION */}
                    <section ref={offersRef} className={`py-5 bus-section-container ${offersInView ? 'is-visible' : ''}`}>
                        <h2 className="bus-section-title">Exclusive Bus Offers</h2>
                        <div className="row g-4">
                            {busDeals.map((deal, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div className={`bus-offer-card bus-offer-card-${deal.type}`}>
                                        <div className="bus-offer-card-header">
                                            <img src={deal.logoUrl} alt={`${deal.operator} logo`} className="bus-operator-logo" />
                                            <span className="bus-discount-badge">{deal.discount}</span>
                                        </div>
                                        <div className="bus-offer-card-body">
                                            <h5 className="bus-card-title">{deal.title}</h5>
                                            <p className="bus-card-text">{deal.description}</p>
                                            <a href="#!" className="stretched-link">Book Now <i className="ri-arrow-right-line"></i></a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* POPULAR ROUTES SECTION */}
                    <section ref={destinationsRef} className={`py-5 bus-section-container ${destinationsInView ? 'is-visible' : ''}`}>
                        <h2 className="bus-section-title">Explore Popular Routes</h2>
                        <div className="row g-4">
                            {popularRoutes.map((dest, index) => (
                                <div className="col-lg-3 col-md-6" key={index}>
                                    <a href="#!" className="bus-destination-card">
                                        <img src={dest.image} alt={dest.name} className="bus-card-img" />
                                        <div className="bus-card-img-overlay">
                                            <h4 className="bus-destination-title">{dest.name}</h4>
                                        </div>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* WHY CHOOSE US SECTION */}
                    <section ref={whyUsRef} className={`py-5 bus-section-container bus-why-us-section ${whyUsInView ? 'is-visible' : ''}`}>
                        <h2 className="bus-section-title">Why Book With Us?</h2>
                        <div className="row g-4">
                            {whyChooseUs.map((item, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div className="bus-why-us-item">
                                        <div className="bus-why-us-icon">
                                            <i className={item.icon}></i>
                                        </div>
                                        <div className="bus-why-us-content">
                                            <h5 className="title">{item.title}</h5>
                                            <p className="text">{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                </div>
            </div>
        </>
    );
};

export default SearchBus;
